import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { personKeys, roleKeys } from "@/lib/query";
import type { RoleCreateInput, RoleStatus, RoleUpdateInput } from "@/schemas/roles";

export interface Role {
  id: string;
  name: string;
  description: string | null;
  pillarId: string | null;
  pillarName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  roleTeamId: string | null;
  roleTeamName: string | null;
  jobDescriptionUrl: string | null;
  status: RoleStatus;
  createdAt: string;
  updatedAt: string;
}

interface RolesListResponse {
  data: Role[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

interface PageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

interface RoleFilters {
  search: string;
  statuses: string[];
  departmentId: string;
  roleTeamId: string;
  /** Only roles with no one assigned (org-health "unfilled roles"). */
  vacant?: boolean;
  sort?: string;
  dir?: "asc" | "desc";
}

export function useRoles(filters: RoleFilters, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...roleKeys.list(filters), pageRequest] as const,
    queryFn: async (): Promise<RolesListResponse> => {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (pageRequest.cursor) {
        params.set("cursor", pageRequest.cursor);
        params.set("direction", pageRequest.direction);
      }
      if (filters.search) params.set("search", filters.search);
      if (filters.statuses.length) params.set("status", filters.statuses.join(","));
      if (filters.departmentId) params.set("departmentId", filters.departmentId);
      if (filters.roleTeamId) params.set("roleTeamId", filters.roleTeamId);
      if (filters.vacant) params.set("vacant", "1");
      if (filters.sort) {
        params.set("sort", filters.sort);
        params.set("dir", filters.dir ?? "asc");
      }
      const res = await fetch(`/api/roles?${params}`);
      if (!res.ok) throw new Error("Failed to fetch roles");
      return res.json();
    },
  });
}

export function useRole(roleId: string) {
  return useQuery({
    queryKey: roleKeys.detail(roleId),
    queryFn: async (): Promise<Role | null> => {
      const res = await fetch(`/api/roles/${roleId}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch role");
      const json = await res.json();
      return json.data;
    },
  });
}

export interface RoleHolder {
  id: string;
  name: string;
  title: string | null;
  isPrimary: boolean;
}

export function useRoleHolders(roleId: string) {
  return useQuery({
    queryKey: roleKeys.holders(roleId),
    queryFn: async (): Promise<RoleHolder[]> => {
      const res = await fetch(`/api/roles/${roleId}/holders`);
      if (!res.ok) throw new Error("Failed to fetch role employees");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: RoleCreateInput) => {
      const res = await fetch("/api/roles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Create failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      toast.success("Role created");
    },
    onError: () => toast.error("Failed to create role"),
  });
}

export function useUpdateRole(roleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: RoleUpdateInput) => {
      const res = await fetch(`/api/roles/${roleId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Update failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      // Person tables, dialogs, and profiles render each holder's role names —
      // refresh persons so a rename doesn't leave stale role labels there.
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      toast.success("Role updated");
    },
    onError: () => toast.error("Failed to update role"),
  });
}
